import { useState } from 'react'
import { ArrowLeft } from 'lucide-react'
import { useViewOnly } from '@/components/templates/app-shell'
import { DataTableBlock, ListPage } from '@/components/templates/list-page'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { queueColumns } from './unmatched/columns'
import { QueueToolbar } from './unmatched/QueueToolbar'
import { IgnoreDialog, LinkPicker } from './unmatched/QueueDialogs'
import {
  useQueueSelection,
  useUnmatchedAction,
  useUnmatchedItems,
} from './unmatched/useUnmatchedItems'
import type { UnmatchedItem } from './unmatched/types'
import { connectionError } from './connections/useAddConnection'

export function UnmatchedItemsPage({
  canViewConnections = false,
}: {
  canViewConnections?: boolean
}) {
  const state = useUnmatchedItems()
  const viewOnly = useViewOnly()
  const mutation = useUnmatchedAction()
  const selection = useQueueSelection(state.rows.data?.unmatched_items ?? [])
  const [link, setLink] = useState<UnmatchedItem | null>(null)
  const [ignore, setIgnore] = useState<{ ids: number[]; row?: UnmatchedItem } | null>(null)
  const close = () => {
    setLink(null)
    setIgnore(null)
    mutation.reset()
  }
  const columns = queueColumns({
    sortable: state.sortable,
    selection,
    viewOnly,
    pending: mutation.isPending,
    onLink: (row) => {
      mutation.reset()
      setLink(row)
    },
    onAction: (row, action) => mutation.mutate({ row, action }),
    onIgnore: (row) => {
      mutation.reset()
      setIgnore({ ids: [row.id], row })
    },
  })
  return (
    <>
      {canViewConnections && (
        <div className="mx-auto w-full max-w-[1200px] px-6 pt-6">
          <Button asChild variant="link" size="inline">
            <a href="/settings/connections">
              <ArrowLeft className="mr-1 size-3.5" strokeWidth={1.5} />
              Connections
            </a>
          </Button>
        </div>
      )}
      <ListPage
        title="Unmatched items"
        search={{
          value: state.search,
          onChange: (value) => {
            selection.clear()
            state.setSearch(value)
          },
          placeholder: 'Search by name, email or organisation',
        }}
        toolbar={
          <QueueToolbar
            state={state}
            selected={viewOnly ? 0 : selection.ids.length}
            onClear={selection.clear}
            onIgnore={() => {
              mutation.reset()
              setIgnore({ ids: selection.ids })
            }}
          />
        }
      >
        {mutation.error && !link && !ignore && <Alert>{connectionError(mutation.error)}</Alert>}
        {/* Fixed widths keep the queue compact; long names and reasons wrap or truncate. */}
        <div className="[&_table]:min-w-[58rem] [&_table]:table-fixed">
          <DataTableBlock
            table={{
              rows: state.rows.data?.unmatched_items ?? [],
              columns,
              cellClassName: 'py-2',
              getRowId: (row) => row.id,
              getRowLabel: (row) => row.name_snapshot,
              state: queueState(state.rows.isError, state.rows.isPending),
              loadingTitle: 'Loading unmatched items…',
              errorTitle: 'Could not load unmatched items',
              errorDescription: 'Refresh the page to try again.',
              emptyTitle: 'Nothing waiting',
            }}
          />
        </div>
      </ListPage>
      {link && <LinkPicker key={link.id} row={link} mutation={mutation} onClose={close} />}
      {ignore && (
        <IgnoreDialog
          {...ignore}
          mutation={mutation}
          onClose={close}
          onSuccess={() => {
            selection.clear()
            close()
          }}
        />
      )}
    </>
  )
}
function queueState(error: boolean, pending: boolean) {
  if (error) return 'error'
  return pending ? 'loading' : 'ready'
}
